import React, { forwardRef, ReactElement, useCallback, useImperativeHandle, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useSwiperAccessibility } from '@hooks/swiper/use-swiper-accessibility';
import { WizardStepProps } from '@components/catalog/my-sizes/wizard-step/wizard-step';
import 'swiper/css/bundle';
import styles from './wizard.module.scss';

type WizardProps = {
  steps: ReactElement<WizardStepProps>[];
  onDone?: (response: unknown) => void;
  slideChangeCallback?: (activeIndex: number) => void;
} & React.HTMLAttributes<HTMLDivElement>;

type RefType = {
  prev: () => void;
  next: () => void;
};

const Wizard = forwardRef<RefType, WizardProps>((props, ref) => {
  const { className, steps, slideChangeCallback, ...rest } = props;
  const { t: tA11y } = useTranslation('a11y');

  const sliderRef = useRef(null);

  const swiperRef = useRef(null);

  const { ariaLiveRef } = useSwiperAccessibility({
    getSlideLabel: (current, total) =>
      tA11y('A11Y.STEP_SLIDER.CURRENT_STEP', { currentStep: current, totalStep: total }),
    getAriaLiveLabel: (start, end, total) =>
      tA11y('A11Y.STEP_SLIDER.CURRENT_STEP', {
        currentStep: start === end ? start : `${start}-${end}`,
        totalStep: total,
      }),
    swiper: swiperRef.current,
    slides: steps,
  });

  const handleAfterInit = (swiper): void => {
    swiperRef.current = swiper;
  };

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) {
      return;
    }
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) {
      return;
    }
    sliderRef.current.swiper.slideNext();
  }, []);

  useImperativeHandle(ref, () => ({ prev: handlePrev, next: handleNext }), []);

  return (
    <div
      className={clsx(styles.wizard, className)}
      {...rest}
    >
      <div
        className="visuallyhidden"
        aria-live="polite"
        aria-atomic="true"
        ref={ariaLiveRef}
      />
      <Swiper
        // @ts-ignore:next-line

        ref={sliderRef}
        slidesPerView={1}
        allowTouchMove={false}
        aria-label={tA11y('A11Y.STEP_SLIDER.ALL_STEPS', { totalSteps: steps.length })}
        onSlideChange={(e) => slideChangeCallback(e.activeIndex)}
        onAfterInit={handleAfterInit}
      >
        {steps.map((step) => (
          <SwiperSlide key={step.key}>{step}</SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
});

Wizard.displayName = 'Wizard';

export default Wizard;
